import { Component, EventEmitter, HostListener, Input, Output } from '@angular/core';
import { BusinessConflict, ConflictChoice, ConflictGuardian, Resolutions } from './conflict.model';

/**
 * S1 (`student_parent_limit`) once the server offers `remove`: the student
 * already has two parents, so the admin unlinks one of them to make room for
 * the new one. Shown inside `app-conflict-resolver`'s dialog, which owns the
 * footer (Cancel / Unlink & save) and the error notices.
 *
 * Every parent gets the same action — unlink from this student; the account is
 * never touched. Another school's parent arrives masked (name + last 4 of the
 * ID and phone) and is rendered as such: the API sends nothing more, and
 * nothing here may try to show it.
 *
 * Choosing only records the answer. The unlink happens on save, in the same
 * request that adds the new parent.
 */
@Component({
  selector: 'app-parent-limit-panel',
  templateUrl: './parent-limit-panel.component.html',
  styleUrls: ['./parent-limit-panel.component.scss'],
})
export class ParentLimitPanelComponent {
  @Input() conflict: BusinessConflict;
  @Input() resolutions: Resolutions = {};
  /** Parents that were not on the list before the server refreshed it. */
  @Input() updatedIds: number[] = [];
  /** A save is running: the choice cannot change under it. */
  @Input() locked = false;

  @Output() choose = new EventEmitter<ConflictChoice>();
  @Output() undo = new EventEmitter<string>();

  /** The parent whose unlink is being confirmed, if any. */
  confirming: ConflictGuardian = null;
  /** Another school's parent needs an explicit tick before the unlink. */
  confirmChecked = false;

  get parents(): ConflictGuardian[] {
    return this.conflict?.parents || [];
  }

  /** What the admin typed — the server echoes it back unchanged. */
  get newParent(): ConflictGuardian {
    return (this.conflict?.submitted_value as ConflictGuardian) || null;
  }

  get chosenId(): number | null {
    return this.resolutions?.student_parent_limit?.user_id ?? null;
  }

  isChosen(p: ConflictGuardian): boolean {
    return this.chosenId !== null && p?.user_id === this.chosenId;
  }

  /** One slot is enough: the other card waits for Undo. */
  isBlockedByOther(p: ConflictGuardian): boolean {
    return this.chosenId !== null && !this.isChosen(p);
  }

  isUpdated(p: ConflictGuardian): boolean {
    return !!this.updatedIds?.includes(p?.user_id);
  }

  /** In-school parents only: who else they hold here. */
  childNames(p: ConflictGuardian): string {
    return (p?.children_in_school || []).map(child => child.name).join(', ');
  }

  phone(p: ConflictGuardian): string {
    return p?.parent_phone_number
      || ((p?.parent_country_code || '') + (p?.parent_phone || ''))
      || '-';
  }

  masked(last4: string): string {
    return last4 ? '•••• ' + last4 : '-';
  }

  askUnlink(p: ConflictGuardian): void {
    if (this.locked || this.chosenId !== null) {
      return;
    }
    this.confirming = p;
    this.confirmChecked = false;
  }

  get canConfirm(): boolean {
    return !!this.confirming && (!!this.confirming.in_school || this.confirmChecked);
  }

  confirmUnlink(): void {
    if (!this.canConfirm) {
      return;
    }
    this.choose.emit({ field: 'student_parent_limit', source: 'remove', user_id: this.confirming.user_id });
    this.keep();
  }

  keep(): void {
    this.confirming = null;
    this.confirmChecked = false;
  }

  undoChoice(): void {
    if (!this.locked) {
      this.undo.emit('student_parent_limit');
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.keep();
  }

  trackByUserId(index: number, p: ConflictGuardian): number {
    return p?.user_id ?? index;
  }
}
